import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import api, { getApiError, getStructuredApiError } from '../../../lib/api'
import { translateApiError } from '../../../lib/apiErrors'
import { useAuthStore } from '../../../store/authStore'
import { useToastStore } from '../../../store/toastStore'
import { useAccountStore } from '../../../store/accountStore'

interface ActiveSubscriptionsConflict {
  code: 'active_subscriptions'
  message: string
  modules: string[]
}

interface LoginData {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  first_name: string
  last_name: string
}

interface PasswordChangeData {
  current_password: string
  new_password: string
}

export function useLogin() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const { addToast } = useToastStore()
  const { t } = useTranslation('auth')

  return useMutation({
    mutationFn: async (data: LoginData) => {
      await login(data.email, data.password)
    },
    onSuccess: () => {
      addToast(t('login.success'), 'success')
      navigate('/dashboard')
    },
    onError: (error: unknown) => {
      addToast(translateApiError(error, t), 'error')
    }
  })
}

export function useRegister() {
  const { addToast } = useToastStore()
  const { t } = useTranslation('auth')

  return useMutation({
    mutationFn: async (data: RegisterData) => {
      const response = await api.post('/auth/register', data)
      return response.data
    },
    onSuccess: () => {
      addToast(t('register.success'), 'success')
    },
    onError: (error: any) => {
      addToast(translateApiError(error, t) || t('register.errorFallback'), 'error')
    }
  })
}

export function useLogout() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const { addToast } = useToastStore()
  const { setCurrent } = useAccountStore()
  const { t } = useTranslation('common')

  return useMutation({
    mutationFn: async () => {
      // Clears httpOnly refresh cookie + revokes server session
      await api.post('/auth/logout')
    },
    onSuccess: () => {
      logout()
      setCurrent(undefined)
      addToast(t('nav.logoutSuccess'), 'success')
      navigate('/auth/login')
    },
    onError: () => {
      // Still clear local session if the cookie/API call failed
      logout()
      setCurrent(undefined)
      navigate('/auth/login')
    }
  })
}

export function useChangePassword() {
  const { addToast } = useToastStore()
  const { t } = useTranslation('profile')

  return useMutation({
    mutationFn: async (data: PasswordChangeData) => {
      const response = await api.post('/auth/change-password', data)
      return response.data
    },
    onSuccess: () => {
      addToast(t('security.changePassword.toasts.success'), 'success')
    },
    onError: (error: unknown) => {
      addToast(translateApiError(error, t), 'error')
    }
  })
}

export function useDeleteAccount() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const { addToast } = useToastStore()
  const { t } = useTranslation('auth')
  const [subscriptionConflict, setSubscriptionConflict] = useState<ActiveSubscriptionsConflict | null>(null)

  const { mutate, isPending } = useMutation({
    mutationFn: async (confirmCancel: boolean) => {
      const url = confirmCancel ? '/auth/delete-account?confirm_cancel=true' : '/auth/delete-account'
      const response = await api.delete(url)
      return response.data
    },
    onSuccess: () => {
      setSubscriptionConflict(null)
      logout()
      addToast(t('deleteAccount.success'), 'success')
      navigate('/auth/login')
    },
    onError: (error: unknown) => {
      const conflict = getStructuredApiError<ActiveSubscriptionsConflict>(error, 'active_subscriptions')
      if (conflict) {
        setSubscriptionConflict(conflict)
        return
      }
      addToast(getApiError(error, t('deleteAccount.errorFallback')), 'error')
    },
  })

  return {
    mutate,
    isPending,
    subscriptionConflict,
    clearConflict: () => setSubscriptionConflict(null),
  }
} 